import { GenericMetaNode } from "./GenericMetaNode";
import { PAny } from "../properties/PAny";
import { MaterialNamesList } from "../../ui/inputs/MaterialNamesList";
import { Model3dInfos } from "../../ui/inputs/Model3dInfos";

import { json, MetadataRow } from "../../global";
import { PVector3 } from "../properties/PVector3";
import { PBoolean } from "../properties/PBoolean";
import { quat, v3 } from "oc-threejs/short3";
import { isEqualEpsilon } from "oc-commons/Commons";
import { filter, remove } from "oc-threejs/Scenes";
import { debounce } from "lodash";
import { Euler, Object3D } from "three";
import { Jaco3d } from "oc-jaco3d/arrangement3d/Jaco3d";

function isObject(data: json): data is { [key: string]: json } {
  return data != null && typeof data === "object";
}

function isV3Json(data: json): data is { x: number; y: number; z: number } {
  return isObject(data) && [data.x, data.y, data.z].every((d) => Number.isFinite(d));
}

function isQuatJson(data: json): data is { w: number; x: number; y: number; z: number } {
  return isObject(data) && [data.w, data.x, data.y, data.z].every((d) => Number.isFinite(d));
}

function isBoolean(data: json): data is boolean {
  return data != null && typeof data == "boolean";
}

export class Model extends GenericMetaNode {
  ModelData: json;
  object3d: Object3D;
  Key: string;
  debouncedUpdate = debounce(this.updateScenes, 500);

  get _mdPosition(): number[] {
    if (isObject(this.ModelData) && isV3Json(this.ModelData.Position)) {
      const v = this.ModelData.Position;
      return [v.x, v.y, v.z];
    } else {
      return null;
    }
  }

  set _mdPosition(values: number[]) {
    if (isObject(this.ModelData) && values.length == 3) {
      this.modelDataChanged("Position", {
        x: values[0],
        y: values[1],
        z: values[2],
      });
    }
  }

  get _mdScale(): number[] {
    if (isObject(this.ModelData) && isV3Json(this.ModelData.Scale)) {
      const v = this.ModelData.Scale;
      return [v.x, v.y, v.z];
    } else {
      return null;
    }
  }

  set _mdScale(values: number[]) {
    if (isObject(this.ModelData) && values.length == 3) {
      this.modelDataChanged("Scale", {
        x: values[0],
        y: values[1],
        z: values[2],
      });
    }
  }

  get _mdGridPosition(): number[] {
    if (isObject(this.ModelData) && isV3Json(this.ModelData.GridPosition)) {
      const v = this.ModelData.GridPosition;
      return [v.x, v.y, v.z];
    } else {
      return null;
    }
  }

  set _mdGridPosition(values: number[]) {
    if (isObject(this.ModelData) && values.length == 3) {
      this.modelDataChanged("GridPosition", {
        x: values[0],
        y: values[1],
        z: values[2],
      });
    }
  }

  get _mdRotation(): number[] {
    if (isObject(this.ModelData) && isQuatJson(this.ModelData.Rotation)) {
      const v = (quat() as any).fromJSON(this.ModelData.Rotation);

      return (v3() as any)
        .setFromEuler(new Euler().setFromQuaternion(v, "XYZ"))
        .multiplyScalar((180 / Math.PI) * 1000000)
        .round()
        .multiplyScalar(1 / 1000000)
        .toArray();
    } else {
      return null;
    }
  }

  set _mdRotation(values: number[]) {
    if (isObject(this.ModelData) && values.length == 3) {
      const r = v3(...values) as any;
      r.multiplyScalar(Math.PI / 180);
      this.modelDataChanged(
        "Rotation",
        (quat() as any).setFromEuler(new Euler(r.x, r.y, r.z)).toJSON(),
      );
    }
  }

  get _mdAutoCenter(): boolean {
    return (
      isObject(this.ModelData) && isBoolean(this.ModelData.AutoCenter) && this.ModelData.AutoCenter
    );
  }

  set _mdAutoCenter(value: boolean) {
    if (isObject(this.ModelData)) {
      this.modelDataChanged("AutoCenter", value);
    }
  }

  modelDataChanged(key: string, newValue: json) {
    const old = this.ModelData[key];
    if (!isEqualEpsilon(old, newValue)) {
      this.ModelData[key] = newValue;
      this.debouncedUpdate();
    }
  }

  updateScenes() {
    //Update Preview
    // @ts-ignore
    APP.sceneNode.resetScene();
    //Update Scene Elements
    const instances = filter(
      STAGE.scene,
      (o) => (o as Jaco3d).jaco?.getMeta()?.Model?.Key == this.Key,
    );
    instances.forEach((o) => {
      remove(o);
      (o as Jaco3d).jaco.jaco3d = null;
    });
    if (instances.length > 0) {
      // @ts-ignore
      APP.dirty();
    }
  }

  // constructor(metatable, meta) {
  //     super(metatable, meta);
  // }

  // fromMetadataRow(row) {
  //     super.fromMetadataRow(row);
  //     const mmrList = this.findProperty('_ModelMaterialRelation') as PNodeList<any>;
  //     const metaData = row.ModelMaterialRelation ?? [];
  //     metaData.forEach(row => {
  //         const node = mmrList.createInstance(this).fromMetadataRow(row);
  //         mmrList.add(node);
  //     });
  //     return this;
  // }

  // updateScene() {
  //     super.updateScene();
  //     this.meta.ModelMaterialRelation.forEach(mmr=>mmr.updateScene());
  // }

  /**
   * Dient der Strukturierung und Ergänzung vorhandener Properties, welche mittels Reflektion ermittelt werden
   */

  toMetadataRow(): MetadataRow {
    const row = super.toMetadataRow();
    const defaults = APP.richMetadata?.getDefaultModelData?.(row as any);
    row["ModelData"] = { ...this.meta["ModelData"] };
    if (defaults) {
      const plainJsonDefaults = JSON.parse(JSON.stringify(defaults));
      for (const key in plainJsonDefaults) {
        if (isEqualEpsilon(row["ModelData"][key], plainJsonDefaults[key])) {
          delete row["ModelData"][key];
        }
      }
    }
    return row;
  }

  createProperties(): any {
    const properties = [
      ...super.createProperties(),
      new PAny({
        name: "_3dInfos",
        label: null,
        inputComponent: Model3dInfos,
        metadataPersistent: false,
        node: this,
      }),
      new PAny({
        name: "_Materialien",
        label: null,
        inputComponent: MaterialNamesList,
        metadataPersistent: false,
        node: this,
      }),
      new PVector3({
        name: "_mdPosition",
        label: "Position",
        node: this,
        metadataPersistent: false,
      }),
      new PVector3({
        name: "_mdGridPosition",
        label: "GridPosition",
        node: this,
        metadataPersistent: false,
      }),
      new PVector3({
        name: "_mdScale",
        label: "Scale",
        default: [1, 1, 1],
        node: this,
        metadataPersistent: false,
      }),
      new PVector3({
        name: "_mdRotation",
        label: "Rotation",
        node: this,
        metadataPersistent: false,
      }),
      new PBoolean({
        name: "_mdAutoCenter",
        label: "AutoCenter",
        node: this,
        metadataPersistent: false,
      }),
    ];
    return this.orderAndGroup(
      // @ts-ignore
      [
        { group: "Preview", open: true },
        { name: "Path" },
        { group: "Hauptparameter", open: true },
        { name: "Key" },
        { unused: true },
      ]
        .concat([
          { group: "Positionierung", open: false },
          { name: "_mdScale" },
          { name: "_mdRotation" },
          { name: "_mdAutoCenter" },
          { name: "_mdGridPosition" },
          { name: "_mdPosition" },
        ])
        .concat(
          // @ts-ignore
          APP.metadata.ModelMaterialRelation
            ? [{ group: "Materialaustausch" }, { name: "ModelMaterialRelation" }]
            : [],
        )
        .concat([
          { group: "Materialnamen im Modell", open: false },
          { name: "_Materialien" },
          { group: "3D-Infos", open: false },
          { name: "_3dInfos" },
        ]),
      properties,
    );
  }
}
